import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, CheckCircle2, Receipt, Network, FileCheck, Search, Layers, Copy, Stethoscope, FileText, Clock, ShieldCheck, TrendingDown, Sparkles } from 'lucide-react';
const capabilities = [{
  icon: Receipt,
  name: 'State Fee Schedule Application',
  description: 'Accurate application of state-mandated fee schedules across every jurisdiction in the country.'
}, {
  icon: Network,
  name: 'PPO Network Pricing',
  description: 'Access to leading national and regional PPO networks for additional negotiated discounts beyond fee schedules.'
}, {
  icon: Search,
  name: 'Usual & Customary Review',
  description: "Where fee schedules don't apply, U&C analysis ensures fair pricing based on regional market data."
}, {
  icon: FileCheck,
  name: 'Code Editing & Compliance',
  description: 'Automated and clinical review for proper CPT, HCPCS, and ICD-10 coding, modifiers, and billing rules.'
}, {
  icon: Copy,
  name: 'Duplicate Detection',
  description: 'Cross-bill and cross-claim duplicate identification — catching what manual review misses.'
}, {
  icon: Layers,
  name: 'Unbundling & Upcoding',
  description: 'Clinical edits to identify unbundled procedures, upcoded services, and improper bundling violations.'
}, {
  icon: Stethoscope,
  name: 'Specialty Bill Review',
  description: 'Expert review of complex bills: hospital, surgical, anesthesia, durable medical equipment, and pharmacy.'
}, {
  icon: FileText,
  name: 'Explanation of Review (EOR)',
  description: 'Clear, defensible EOR documentation for every bill — supporting providers, adjusters, and regulators.'
}];
const benefits = [{
  icon: TrendingDown,
  title: 'Higher savings',
  stat: '62%',
  description: 'Average savings off provider billed charges through fee schedule, PPO, and clinical review combined.'
}, {
  icon: Clock,
  title: 'Fast turnaround',
  stat: '72hr',
  description: 'Standard bills reviewed and processed within 72 hours — most within 24 to 48.'
}, {
  icon: Sparkles,
  title: 'Technology-driven',
  stat: 'AI',
  description: 'AI-assisted screening combined with clinical expertise — every bill gets both layers of review.'
}, {
  icon: ShieldCheck,
  title: 'Compliance assured',
  stat: '50',
  description: "Compliant with workers' compensation billing regulations in all 50 states."
}];
export function MedicalBillReview() {
  return <div className="bg-brand-paper" data-id="element-732">
      {/* Hero */}
      <section className="relative bg-brand-navy pt-40 pb-24 overflow-hidden" data-id="element-733">
        <div className="absolute inset-0 opacity-10" data-id="element-734">
          <div className="absolute inset-0" style={{
          backgroundImage: 'radial-gradient(#C9A24B 1px, transparent 1px)',
          backgroundSize: '40px 40px'
        }} data-id="element-735"></div>
        </div>
        <div className="absolute inset-0 bg-gradient-to-br from-brand-navy via-brand-navy/95 to-brand-navy/80" data-id="element-736"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-737">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-3xl" data-id="element-738">
            <div className="inline-flex items-center space-x-2 bg-brand-gold/15 border border-brand-gold/40 rounded-full px-4 py-1.5 mb-6" data-id="element-739">
              <span className="text-xs font-bold tracking-widest text-brand-gold uppercase" data-id="element-740">
                Managed Care Service
              </span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-medium text-white leading-[1.1] mb-6 text-balance" data-id="element-741">
              Medical{' '}
              <span className="text-brand-gold italic" data-id="element-742">Bill Review</span>
            </h1>
            <p className="text-xl text-white/80 mb-10 leading-relaxed font-light max-w-2xl" data-id="element-743">
              Smart technology and expert clinical review combine to ensure
              every medical bill is priced accurately, coded correctly, and
              compliant in every jurisdiction.
            </p>
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4" data-id="element-744">
              <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold hover:bg-white transition-colors flex items-center" data-id="element-745">
                Request a Demo <ArrowRight className="w-5 h-5 ml-2" data-id="element-746" />
              </Link>
              <a href="tel:8552601053" className="flex items-center text-white border border-white/30 px-8 py-4 rounded font-semibold hover:bg-white/10 transition-colors" data-id="element-747">
                <Phone className="w-5 h-5 mr-2" data-id="element-748" /> 855.260.1053
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Intro */}
      <section className="py-24 bg-white" data-id="element-749">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-16 items-center" data-id="element-750">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} data-id="element-751">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-752">
              The Problem We Solve
            </h2>
            <h3 className="text-4xl font-serif text-brand-navy mb-6 leading-tight" data-id="element-753">
              Pay only what's right. Defend every dollar.
            </h3>
            <p className="text-lg text-gray-600 leading-relaxed mb-6" data-id="element-754">
              Workers' compensation medical bills are complex, jurisdictional,
              and full of opportunity for overpayment. Without rigorous review,
              every claim leaks dollars — and creates compliance risk.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed mb-8" data-id="element-755">
              Our hybrid technology-plus-clinical-review program catches what
              others miss: improperly coded charges, duplicate bills, upcharges,
              and treatments outside the medical necessity envelope. Every
              reduction is defensible and documented.
            </p>
            <ul className="space-y-4" data-id="element-756">
              {['AI-assisted screening on 100% of bills', 'Clinical review by experienced bill reviewers', 'Fee schedule + PPO + U&C layered savings', 'EOR documentation that holds up to dispute'].map(item => <li key={item} className="flex items-start" data-id="element-757">
                  <CheckCircle2 className="w-6 h-6 text-brand-gold mr-3 flex-shrink-0 mt-0.5" data-id="element-758" />
                  <span className="text-gray-800 font-medium" data-id="element-759">{item}</span>
                </li>)}
            </ul>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          x: 30
        }} whileInView={{
          opacity: 1,
          x: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.7
        }} className="relative" data-id="element-760">
            <div className="absolute -inset-4 bg-brand-gold/20 rounded-2xl transform rotate-2" data-id="element-761"></div>
            <img src="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&auto=format&fit=crop&w=2400&q=90" alt="Detailed financial review at a desk with documents" className="relative rounded-2xl shadow-2xl h-[560px] w-full object-cover" data-id="element-762" />
          </motion.div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="py-24 bg-brand-paper" data-id="element-763">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-764">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-765">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-766">
              What's Included
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy mb-6" data-id="element-767">
              Every layer of bill review.
            </h3>
            <p className="text-lg text-gray-600" data-id="element-768">
              From simple physician visits to complex hospital and surgical
              bills — every line item gets the right level of scrutiny.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-769">
            {capabilities.map((cap, i) => {
            const Icon = cap.icon;
            return <motion.div key={cap.name} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.4,
              delay: i * 0.05
            }} className="bg-white rounded-xl p-6 shadow-sm hover:shadow-xl transition-shadow border border-gray-100 group" data-id="element-770">
                  <div className="w-12 h-12 rounded-lg bg-brand-navy/5 text-brand-navy flex items-center justify-center mb-5 group-hover:bg-brand-gold group-hover:text-white transition-colors" data-id="element-771">
                    <Icon className="w-6 h-6" data-id="element-772" />
                  </div>
                  <h4 className="font-serif font-bold text-lg text-brand-navy mb-2" data-id="element-773">
                    {cap.name}
                  </h4>
                  <p className="text-sm text-gray-600 leading-relaxed" data-id="element-774">
                    {cap.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white" data-id="element-775">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-776">
          <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-777">
            <h2 className="text-brand-gold font-semibold tracking-wider uppercase text-sm mb-3" data-id="element-778">
              Outcomes
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif text-brand-navy" data-id="element-779">
              Built for measurable savings.
            </h3>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-780">
            {benefits.map((b, i) => {
            const Icon = b.icon;
            return <motion.div key={b.title} initial={{
              opacity: 0,
              y: 20
            }} whileInView={{
              opacity: 1,
              y: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: i * 0.1
            }} className="bg-brand-navy rounded-xl p-8 text-white relative overflow-hidden" data-id="element-781">
                  <div className="absolute -top-4 -right-4 w-24 h-24 bg-brand-gold/10 rounded-full blur-2xl" data-id="element-782"></div>
                  <Icon className="w-8 h-8 text-brand-gold mb-6" data-id="element-783" />
                  <div className="text-5xl font-serif font-bold text-brand-gold mb-2" data-id="element-784">
                    {b.stat}
                  </div>
                  <div className="font-bold text-white mb-3" data-id="element-785">{b.title}</div>
                  <p className="text-sm text-white/70 leading-relaxed" data-id="element-786">
                    {b.description}
                  </p>
                </motion.div>;
          })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-brand-navy py-20 relative overflow-hidden" data-id="element-787">
        <div className="absolute top-0 left-0 w-full h-1 bg-brand-gold" data-id="element-788"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-789">
          <h2 className="text-4xl md:text-5xl font-serif text-white mb-6 leading-tight" data-id="element-790">
            See your savings opportunity.
          </h2>
          <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto" data-id="element-791">
            Send us a sample of recent bills and we'll show you exactly what
            Arissa's medical bill review could have saved.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-792">
            <Link to="/contact" className="bg-brand-gold text-brand-navy px-8 py-4 rounded font-semibold text-lg hover:bg-white transition-colors w-full sm:w-auto" data-id="element-793">
              Request a Free Bill Audit
            </Link>
            <a href="tel:8552601053" className="flex items-center justify-center text-white border border-white/30 px-8 py-4 rounded font-semibold text-lg hover:bg-white/10 transition-colors w-full sm:w-auto" data-id="element-794">
              <Phone className="w-5 h-5 mr-2" data-id="element-795" /> 855.260.1053
            </a>
          </div>
        </div>
      </section>
    </div>;
}